import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  TextField,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import {
  RadioButtonUnchecked as BufferIcon,
  Timeline as DistanceIcon,
  Clear as ClearIcon,
  Place as PlaceIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const AnalysisTools = ({ map, analysisMode, setAnalysisMode, analysisResult, setAnalysisResult }) => {
  const [bufferRadius, setBufferRadius] = useState(5000); // meters
  
  // Handle analysis mode change
  const handleModeChange = (event, newMode) => {
    setAnalysisMode(newMode);
    setAnalysisResult(null);
  };
  
  // Clear analysis
  const handleClear = () => {
    setAnalysisMode(null);
    setAnalysisResult(null);
  };
  
  // Format distance for display
  const formatDistance = (meters) => {
    if (meters < 1000) {
      return `${meters.toFixed(0)} m`;
    }
    return `${(meters / 1000).toFixed(2)} km`;
  };
  
  return (
    <Box>    
      {/* Analysis Mode Selection */}
      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
        Select Analysis Tool
      </Typography>
      
      <ToggleButtonGroup
        value={analysisMode}
        exclusive
        onChange={handleModeChange}
        aria-label="analysis mode"
        fullWidth
        sx={{ mb: 3 }}
      >
        <ToggleButton value="buffer" aria-label="buffer analysis">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
            <BufferIcon />
            <Typography variant="caption" sx={{ mt: 0.5 }}>Buffer</Typography>
          </Box>
        </ToggleButton>
        
        <ToggleButton value="distance" aria-label="distance measurement">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
            <DistanceIcon />
            <Typography variant="caption" sx={{ mt: 0.5 }}>Distance</Typography>
          </Box>
        </ToggleButton>
      </ToggleButtonGroup>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Tool Instructions */}
      {!analysisMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="caption">
            Select a tool above to begin spatial analysis on the map.
          </Typography>
        </Alert>
      )}
      
      {/* Buffer Analysis Options */}
      {analysisMode === 'buffer' && (
        <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd' }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="primary">
            Buffer Zone Analysis
          </Typography>
          
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
            Click anywhere on the map to create a buffer zone and find nearby locations.
          </Typography>
          
          <TextField
            fullWidth
            type="number"
            label="Buffer Radius (meters)"
            value={bufferRadius}
            onChange={(e) => setBufferRadius(Number(e.target.value))}
            InputProps={{
              inputProps: { min: 100, max: 50000, step: 100 }
            }}
            helperText="Set the radius for the buffer zone (100m - 50km)"
            sx={{ mb: 2 }}
          />
        </Paper>
      )}
      
      {/* Distance Measurement Options */}
      {analysisMode === 'distance' && (
        <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: '#fff3e0' }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="warning.dark">
            Distance Measurement
          </Typography>
          
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
            Click two points on the map to measure the distance between them.
          </Typography>
        </Paper>
      )}
      
      {/* Analysis Results */}
      {analysisResult && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Analysis Results
          </Typography>
          
          {/* Buffer Results */}
          {analysisResult.count !== undefined && (
            <Paper elevation={3} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {analysisResult.count}
                </Typography>
                <Chip label="Locations Found" color="primary" />
              </Box>
              
              <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                Locations within {formatDistance(analysisResult.buffer?.radius)} radius
              </Typography>
              
              <Divider sx={{ my: 1 }} />
              
              <Typography variant="caption" color="textSecondary">
                <strong>Center Point:</strong>
                <br />Lat: {analysisResult.buffer?.center?.lat.toFixed(6)}
                <br />Lng: {analysisResult.buffer?.center?.lng.toFixed(6)}
              </Typography>
            </Paper>
          )}
          
          {/* Distance Results */}
          {analysisResult.distance_meters !== undefined && (
            <Paper elevation={3} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Measured Distance
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
                <Paper sx={{ p: 2, flexGrow: 1, bgcolor: 'primary.main', color: 'white' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Meters</Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {analysisResult.distance_meters.toLocaleString()}
                  </Typography>
                </Paper>
                
                <Paper sx={{ p: 2, flexGrow: 1, bgcolor: 'secondary.main', color: 'white' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Kilometers</Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {analysisResult.distance_kilometers.toFixed(2)}
                  </Typography>
                </Paper>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <PlaceIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Point 1"
                    secondary={`Lat: ${analysisResult.point1?.lat.toFixed(6)}, Lng: ${analysisResult.point1?.lng.toFixed(6)}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PlaceIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Point 2"
                    secondary={`Lat: ${analysisResult.point2?.lat.toFixed(6)}, Lng: ${analysisResult.point2?.lng.toFixed(6)}`}
                  />
                </ListItem>
              </List>
            </Paper>
          )}
        </Box>
      )}
      
      {/* Clear Button */}
      {(analysisMode || analysisResult) && (
        <>
          <Divider sx={{ my: 3 }} />
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<ClearIcon />}
            onClick={handleClear}
          >
            Clear Analysis
          </Button>
        </>
      )}
      
      {/* Additional Info */}
      <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          Available Tools
        </Typography>
        <Typography variant="caption" component="div" color="textSecondary" sx={{ mb: 1 }}>
          <strong>Buffer Analysis:</strong> Find all locations within a specified radius from a point
        </Typography>
        <Alert severity="success" icon={<InfoIcon />}>
          <Typography variant="caption">
            <strong>How to use:</strong>
            <br />• Click on the map or on a marker
            <br />• A circle will show the buffer zone
            <br />• Results will display locations within the zone
          </Typography>
        </Alert>
        <Typography variant="caption" component="div" color="textSecondary">
          <strong>Distance Measurement:</strong> Calculate the straight-line distance between two points
        </Typography>
        <Alert severity="warning" icon={<InfoIcon />}>
          <Typography variant="caption">
            <strong>How to use:</strong>
            <br />• Click first point on the map
            <br />• Click second point
            <br />• Distance will be calculated automatically
            <br />• Click "Clear" to start new measurement
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default AnalysisTools;
